import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
    FiBook,
    FiBookOpen,
    FiFileText,
    FiCheckCircle,
    FiXCircle,
    FiClock,
    FiBarChart2,
    FiPieChart,
    FiTrendingUp,
    FiEye,
    FiSearch,
    FiFilter,
    FiChevronDown,
    FiChevronUp,
    FiUsers,
    FiTarget,
    FiHash,
    FiGrid,
    FiList,
    FiPercent,
    FiArrowLeft
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import he from "he";
import parse from "html-react-parser";

import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";

const PaperOverview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [overviewData, setOverviewData] = useState(null);
    const [questionsData, setQuestionsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('structure');
    const [expandedSubjects, setExpandedSubjects] = useState({});
    const [expandedChapters, setExpandedChapters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('all');
    const [expandedQuestionId, setExpandedQuestionId] = useState(null);
    const [showStudentModal, setShowStudentModal] = useState(false);
    const [studentTab, setStudentTab] = useState("correct");
    const [studentData, setStudentData] = useState({
        correct: { count: 0, students: [] },
        wrong: { count: 0, students: [] },
        unanswered: { count: 0, students: [] }
    });
    const [activeQuestionId, setActiveQuestionId] = useState(null);

    const goToChapterAnalysis = (subjectId) => {
        navigate(`/chapter-analysis/${subjectId}`, {
            state: {
                paperId: id
            }
        });
    };

    async function fetchPaperOverview() {
        setLoading(true);
        try {
            const loggedInUser = localStorage.getItem("loggedInUser");
            const token = localStorage.getItem("authToken");
            const programType = "getPaperOverviewSubjectWise";

            const fd = new FormData();
            fd.append("loggedInUser", loggedInUser);
            fd.append("token", token);
            fd.append("programType", programType);
            fd.append("paperId", id);

            const response = await api.post("overView/overView", fd);
            // console.log("Overview API Response:", response.data);

            if (response.data.status === true && response.data.data) {
                setOverviewData(response.data.data);
                setExpandedSubjects({});
            }
        } catch (error) {
            console.error("Error fetching overview:", error);
        } finally {
            setLoading(false);
        }
    }

    async function fetchQuestions() {
        setQuestionsLoading(true);
        try {
            const fd = new FormData();
            fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
            fd.append("token", localStorage.getItem("authToken"));
            fd.append("programType", "printQuestions");
            fd.append("paperId", id);

            const response = await api.post("picQuestion/picQuestionCurl", fd);
            // console.log("Fetched Questions:", response.data);

            if (response.data && Array.isArray(response.data)) {
                setQuestionsData(response.data);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
        } finally {
            setQuestionsLoading(false);
        }
    }

    async function fetchStudents(questionId, defaultTab = "correct") {
        try {
            const fd = new FormData();
            fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
            fd.append("token", localStorage.getItem("authToken"));
            fd.append("programType", "getAllStudentsByQuestion");
            fd.append("paperId", id);
            fd.append("questionId", questionId);

            const response = await api.post("picQuestion/picQuestionCurl", fd);
            // console.log("Fetched Students for Question:", response);

            if (response.data) {
                setStudentData({
                    correct: response.data.correct || { count: 0, students: [] },
                    wrong: response.data.wrong || { count: 0, students: [] },
                    unanswered: response.data.unanswered || { count: 0, students: [] }
                });

                setActiveQuestionId(questionId);
                setStudentTab(defaultTab);
                setShowStudentModal(true);
            }
        } catch (error) {
            console.error("❌ Error fetching students:", error);
        }
    }

    useEffect(() => {
        fetchPaperOverview();
        fetchQuestions();
    }, [id]);

    const renderLatexText = (text, layout = "single", fontSize = "16px") => {
        if (!text) return null;

        let decodedText = he.decode(text);
        decodedText = decodedText.replace(/<br\s*\/?>/gi, "\n");

        const latexAndImageRegex =
            /\\\((.*?)\\\)|\\\[(.*?)\\\]|<img[^>]*\bsrc=(["'])(.*?)\3[^>]*>|\\begin{align}([\s\S]*?)\\end{align}/g;

        const parts = [];
        let lastIndex = 0;
        let match;
        let key = 0;

        while ((match = latexAndImageRegex.exec(decodedText)) !== null) {
            const [fullMatch, inlineLatex, blockLatex, , imageSrc, alignContent] = match;
            const matchIndex = match.index;

            if (matchIndex > lastIndex) {
                const plainText = decodedText.slice(lastIndex, matchIndex);
                plainText.split("\n").forEach((line, i, arr) => {
                    if (line) parts.push(<span className="3 normal-inline" key={`html-${key++}`}>{parse(line)}</span>);
                    if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
                });
            }

            if (imageSrc) {
                parts.push(
                    <img
                        key={`img-${key++}`}
                        src={imageSrc}
                        alt="Image"
                        style={{
                            maxWidth: `calc(${fontSize} * 18)`,
                            display: "inline",
                            verticalAlign: "middle",
                            marginLeft: "4px",
                            marginRight: "4px",
                        }}
                    />
                );
            } else if (alignContent) {
                parts.push(
                    <BlockMath key={`align-${key++}`}>
                        {`\\begin{align}${alignContent}\\end{align}`}
                    </BlockMath>
                );
            } else if (inlineLatex || blockLatex) {
                parts.push(
                    <InlineMath key={`inline-${key++}`}>
                        {inlineLatex || blockLatex}
                    </InlineMath>
                );
            }

            lastIndex = matchIndex + fullMatch.length;
        }

        if (lastIndex < decodedText.length) {
            const remaining = decodedText.slice(lastIndex);
            remaining.split("\n").forEach((line, i, arr) => {
                if (line) parts.push(<span className="2 normal-inline" key={`html-${key++}`}>{parse(line)}</span>);
                if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
            });
        }

        return <span className="1 " style={{ whiteSpace: "normal", display: "inline" }}>{parts}</span>;
    };

    const toggleSubject = (subjectId) => {
        setExpandedSubjects(prev => ({
            ...prev,
            [subjectId]: !prev[subjectId]
        }));
    };

    const toggleChapter = (chapterId) => {
        setExpandedChapters(prev => ({
            ...prev,
            [chapterId]: !prev[chapterId]
        }));
    };

    const toggleQuestion = (questionId) => {
        setExpandedQuestionId(expandedQuestionId === questionId ? null : questionId);
    };

    const getSubjectStats = () => {
        if (!overviewData?.subjectWiseStats) return [];

        return overviewData.subjectWiseStats.map(subject => ({
            ...subject,
            chapters: overviewData.chapterWiseStats?.filter(ch => ch.subjectId === subject.subjectId) || [],
            topics: overviewData.topicWiseStats?.filter(t => t.subjectId === subject.subjectId) || []
        }));
    };

    const getChapterTopics = (chapterId) => {
        if (!overviewData?.topicWiseStats) return [];
        return overviewData.topicWiseStats.filter(t => t.chapterId === chapterId);
    };

    const filteredQuestions = questionsData.filter(question => {
        const matchesSearch = searchTerm === '' ||
            question.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            question.chapterId?.toString().includes(searchTerm) ||
            question.topicId?.toString().includes(searchTerm);

        const matchesSubject = selectedSubject === 'all' ||
            question.subjectId?.toString() === selectedSubject;

        return matchesSearch && matchesSubject;
    });

    const getOptionLetter = (index) => {
        return String.fromCharCode(65 + index);
    };

    const calculateQuestionStats = (question) => {
        const total = parseInt(question.examCorrectCount || 0) +
            parseInt(question.examWrongCount || 0) +
            parseInt(question.examUnansweredCount || 0);

        const correctPercentage = total > 0 ?
            Math.round((parseInt(question.examCorrectCount || 0) / total) * 100) : 0;
        const wrongPercentage = total > 0 ?
            Math.round((parseInt(question.examWrongCount || 0) / total) * 100) : 0;
        const unansweredPercentage = total > 0 ?
            Math.round((parseInt(question.examUnansweredCount || 0) / total) * 100) : 0;

        return {
            total,
            correctPercentage,
            wrongPercentage,
            unansweredPercentage
        };
    };

    const subjectsForFilter = overviewData?.subjectWiseStats?.map(subject => ({
        value: subject.subjectId,
        label: subject.subjectName
    })) || [];

    if (loading) {
        return (
            <div className="dashboard-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Loading Paper Overview…</div>
                </div>
            </div>
        );
    }

    const pieColors = [
        "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"
    ];

    const questionDistributionData = getSubjectStats().map(subject => ({
        name: subject.subjectName,
        value: subject.totalQuestions
    }));

    const summaryCards = [
        {
            title: "Total Subjects",
            value: overviewData.subjectWiseStats?.length || 0,
            icon: <FiBook size={20} />,
            color: "text-blue-600",
            iconBg: "bg-blue-100"
        },
        {
            title: "Total Chapters",
            value: overviewData.chapterWiseStats?.length || 0,
            icon: <FiBookOpen size={20} />,
            color: "text-green-600",
            iconBg: "bg-green-100"
        },
        {
            title: "Total Topics",
            value: overviewData.topicWiseStats?.length || 0,
            icon: <FiFileText size={20} />,
            color: "text-indigo-600",
            iconBg: "bg-indigo-100"
        },
        {
            title: "Total Questions",
            value: overviewData.subjectWiseStats?.reduce((sum, s) => sum + (s.totalQuestions || 0), 0) || 0,
            icon: <FiTarget size={20} />,
            color: "text-orange-600",
            iconBg: "bg-orange-100"
        }
    ];

    return (
        <div className="dashboard-container">
          
            {/* Summary Cards */}
            <div className="summary-grid">
                {summaryCards.map((card, index) => (
                    <div key={index} className="summary-card">
                        <div className={`summary-icon ${card.iconBg}`}>
                            {card.icon}
                        </div>
                        <div className="summary-content">
                            <div className="summary-title">{card.title}</div>
                            <div className={`summary-value ${card.color}`}>{card.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tabs Navigation */}
            <div className="tabs-container">
                <div className="tabs-wrapper">
                    <button
                        className={`tab-button ${activeTab === 'structure' ? 'active' : ''}`}
                        onClick={() => setActiveTab('structure')}
                    >
                        <FiGrid className="tab-icon" />
                        <span>Exam Structure</span>
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('questions')}
                    >
                        <FiList className="tab-icon" />
                        <span>Questions Analysis</span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            {activeTab === 'structure' && overviewData && (
                <>
                    {/* Subjects Overview */}
                    <div className="section-header">
                        <h2>📚 Subjects Overview</h2>
                        <p>Performance breakdown by subject</p>
                    </div>

                    <div className="subject-cards-grid">
                        {getSubjectStats().map((subject, index) => (
                            <div
                                key={subject.subjectId}
                                className="subject-card"
                                onClick={() => goToChapterAnalysis(subject.subjectId)}
                            >
                                <div className="subject-card-header">
                                    <div className="subject-card-avatar">
                                        {subject.subjectName.charAt(0)}
                                    </div>
                                    <div>
                                        <h4>{subject.subjectName}</h4>
                                        <p>{subject.totalQuestions} questions · {subject.chapters.length} chapters</p>
                                    </div>
                                </div>

                                <div className="subject-stats-grid">
                                    <div className="stat-item">
                                        <div className="stat-value text-green-600">{subject.chapters.length}</div>
                                        <div className="stat-label">Chapters</div>
                                    </div>
                                    <div className="stat-divider"></div>
                                    <div className="stat-item">
                                        <div className="stat-value text-blue-600">{subject.topics.length}</div>
                                        <div className="stat-label">Topics</div>
                                    </div>
                                    <div className="stat-divider"></div>
                                    <div className="stat-item">
                                        <div className="stat-value text-orange-600">{subject.totalQuestions}</div>
                                        <div className="stat-label">Questions</div>
                                    </div>
                                </div>

                                <div className="subject-expandable">
                                    <button
                                        className="expand-toggle"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSubject(subject.subjectId);
                                        }}
                                    >
                                        {expandedSubjects[subject.subjectId] ? 'Show Less' : 'View Chapters'}
                                        {expandedSubjects[subject.subjectId] ?
                                            <FiChevronUp size={16} /> :
                                            <FiChevronDown size={16} />
                                        }
                                    </button>

                                    {expandedSubjects[subject.subjectId] && (
                                        <div className="chapters-list">
                                            {subject.chapters.map(chapter => (
                                                <div key={chapter.chapterId} className="chapter-item">
                                                    <div className="chapter-header">
                                                        <h5>{chapter.chapterName}</h5>
                                                        <span className="chapter-questions">
                                                            {chapter.totalQuestions} Qs
                                                        </span>
                                                    </div>
                                                    
                                                    {expandedChapters[chapter.chapterId] && (
                                                        <div className="topics-list">
                                                            {getChapterTopics(chapter.chapterId).map(topic => (
                                                                <div key={topic.topicId} className="topic-item">
                                                                    <span className="topic-name">{topic.topicName}</span>
                                                                    <span className="topic-meta">
                                                                        {topic.totalQuestions} Qs
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    
                                                    <button
                                                        className="topics-toggle"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleChapter(chapter.chapterId);
                                                        }}
                                                    >
                                                        Topics ({getChapterTopics(chapter.chapterId).length})
                                                        {expandedChapters[chapter.chapterId] ?
                                                            <FiChevronUp size={14} /> :
                                                            <FiChevronDown size={14} />
                                                        }
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Distribution Chart */}
                    <div className="charts-grid">
                        <div className="chart-card">
                            <div className="card-header">
                                <h2><FiPieChart size={18} /> Question Distribution</h2>
                            </div>
                            <div className="card-content">
                                <div className="distribution-container">
                                    <div style={{ width: '100%', height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={questionDistributionData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={80}
                                                    innerRadius={40}
                                                    paddingAngle={2}
                                                    label={({ name, percent }) =>
                                                        `${name} (${Math.round(percent * 100)}%)`
                                                    }
                                                >
                                                    {questionDistributionData.map((_, index) => (
                                                        <Cell
                                                            key={index}
                                                            fill={pieColors[index % pieColors.length]}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value) => [`${value} Questions`, ""]} />
                                                <Legend verticalAlign="bottom" height={36} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'questions' && (
                <>
                    {/* Filter Section */}
                    <div className="section-header">
                        <h2>📝 Questions Analysis</h2>
                        <p>Detailed performance analysis for each question</p>
                    </div>

                    <div className="filter-section">
                        <div className="search-container">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search questions by text, chapter, or topic..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="filter-group">
                            <FiFilter className="filter-icon" />
                            <select
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                                className="subject-select"
                            >
                                <option value="all">All Subjects</option>
                                {subjectsForFilter.map(subject => (
                                    <option key={subject.value} value={subject.value}>
                                        {subject.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Questions List */}
                    {questionsLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Loading questions…</div>
                        </div>
                    ) : filteredQuestions.length === 0 ? (
                        <div className="empty-state">
                            <FiFileText className="empty-icon" />
                            <h3>No questions found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className="questions-container">
                            {filteredQuestions.map((question, index) => {
                                const stats = calculateQuestionStats(question);
                                return (
                                    <div key={question.id} className="question-card">
                                        <div
                                            className="question-header clickable"
                                            onClick={() => toggleQuestion(question.id)}
                                        >
                                            <div className="question-main">
                                                <div className="question-number">
                                                    <span>Q{index + 1}</span>
                                                </div>
                                                <div className="question-content">
                                                    <div className="question-text-preview">
                                                        {renderLatexText(question.question)}
                                                    </div>
                                                    <div className="question-meta">
                                                        <span className="meta-item">
                                                            <FiUsers size={12} />
                                                            {stats.total} students
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="question-actions">
                                                <div className="question-stats">
                                                    <div className="stat-item correct">
                                                        <FiCheckCircle size={13} />
                                                        <span>{question.examCorrectCount || 0}</span>
                                                    </div>
                                                    <div className="stat-item wrong">
                                                        <FiXCircle size={13} />
                                                        <span>{question.examWrongCount || 0}</span>
                                                    </div>
                                                    <div className="stat-item unanswered">
                                                        <FiClock size={13} />
                                                        <span>{question.examUnansweredCount || 0}</span>
                                                    </div>
                                                </div>
                                                <button className="expand-icon">
                                                    {expandedQuestionId === question.id ?
                                                        <FiChevronUp size={18} /> :
                                                        <FiChevronDown size={18} />
                                                    }
                                                </button>
                                            </div>
                                        </div>

                                        {expandedQuestionId === question.id && (
                                            <div className="question-details">
                                                {/* Question Text */}
                                                <div className="detail-section">
                                                    <h4 className="detail-title">Question</h4>
                                                    <div className="detail-content full-question">
                                                        {renderLatexText(question.question)}
                                                    </div>
                                                </div>

                                                {/* Options - 2 Column Layout */}
                                                <div className="detail-section">
                                                    <h4 className="detail-title">Options</h4>
                                                    <div className="options-grid">
                                                        {question.options?.map((option, optIndex) => {
                                                            const isCorrect = getOptionLetter(optIndex) === question.answer?.charAt(1) ||
                                                                option === question.answer;
                                                            return (
                                                                <div
                                                                    key={optIndex}
                                                                    className={`option-item ${isCorrect ? 'correct' : ''}`}
                                                                >
                                                                    <div className="option-header">
                                                                        <div className="option-letter">
                                                                            {getOptionLetter(optIndex)}
                                                                        </div>
                                                                        {isCorrect && (
                                                                            <div className="correct-indicator">
                                                                                <FiCheckCircle size={13} />
                                                                                <span>Correct Answer</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="option-content">
                                                                        {renderLatexText(option)}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Statistics */}
                                                <div className="detail-section">
                                                    <h4 className="detail-title">Performance Statistics</h4>
                                                    <div className="stats-grid detailed">
                                                        <div
                                                            className="stat-card mini clickable"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                fetchStudents(question.id, "correct");
                                                            }}
                                                        >
                                                            <div className="stat-icon-wrapper">
                                                                <div className="stat-icon correct">
                                                                    <FiCheckCircle size={16} />
                                                                </div>
                                                            </div>
                                                            <div className="stat-content">
                                                                <div className="stat-label">Correct Answers</div>
                                                                <div className="stat-value">
                                                                    {question.examCorrectCount || 0} ({stats.correctPercentage}%)
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div
                                                            className="stat-card mini clickable"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                fetchStudents(question.id, "wrong");
                                                            }}
                                                        >
                                                            <div className="stat-icon-wrapper">
                                                                <div className="stat-icon wrong">
                                                                    <FiXCircle size={16} />
                                                                </div>
                                                            </div>
                                                            <div className="stat-content">
                                                                <div className="stat-label">Wrong Answers</div>
                                                                <div className="stat-value">
                                                                    {question.examWrongCount || 0} ({stats.wrongPercentage}%)
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div
                                                            className="stat-card mini clickable"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                fetchStudents(question.id, "unanswered");
                                                            }}
                                                        >
                                                            <div className="stat-icon-wrapper">
                                                                <div className="stat-icon unanswered">
                                                                    <FiClock size={16} />
                                                                </div>
                                                            </div>
                                                            <div className="stat-content">
                                                                <div className="stat-label">Unanswered</div>
                                                                <div className="stat-value">
                                                                    {question.examUnansweredCount || 0} ({stats.unansweredPercentage}%)
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div
                                                            className="stat-card mini clickable"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                fetchStudents(question.id, "correct");
                                                            }}
                                                        >
                                                            <div className="stat-icon-wrapper">
                                                                <div className="stat-icon">
                                                                    <FiUsers size={16} />
                                                                </div>
                                                            </div>
                                                            <div className="stat-content">
                                                                <div className="stat-label">Total Students</div>
                                                                <div className="stat-value">{stats.total}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* Student Modal */}
            {showStudentModal && (
                <div className="modal-backdrop">
                    <div className="student-modal">
                        <div className="modal-header">
                            <h3>Student Performance</h3>
                            <button onClick={() => setShowStudentModal(false)}>✕</button>
                        </div>

                        <div className="modal-tabs">
                            {["correct", "wrong", "unanswered"].map(tab => (
                                <button
                                    key={tab}
                                    className={`modal-tab ${studentTab === tab ? "active" : ""}`}
                                    onClick={() => setStudentTab(tab)}
                                >
                                    {tab.toUpperCase()} ({studentData[tab].count})
                                </button>
                            ))}
                        </div>

                        <div className="modal-content">
                            {studentData[studentTab].students.length === 0 ? (
                                <div className="empty-state">
                                    No students found
                                </div>
                            ) : (
                                <div className="students-list">
                                    {studentData[studentTab].students.map((s, i) => (
                                        <div key={i} className="student-row">
                                            <div className="student-info">
                                                <strong>{s.name}</strong>
                                                <div className="muted">
                                                    Roll: {s.rollNumber || "-"}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => navigate(`/student-overall/${s.studentId}`)}
                                                className="view-button"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Base Container */
                .dashboard-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
                    padding: 1.5rem;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                /* Loading State */
                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 400px;
                }
                
                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e0f2fe;
                    border-top: 3px solid #0369a1;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                .loading-text {
                    margin-top: 12px;
                    color: #64748b;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Header */
                .dashboard-header {
                    margin: 0 auto 2rem;
                    background: white;
                    border-radius: 1rem;
                    padding: 1.5rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .header-content h1 {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 0.5rem;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .header-content p {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin: 0;
                }

                .header-controls {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .back-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.75rem;
                    color: #475569;
                    font-size: 0.875rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .back-button:hover {
                    background: #f1f5f9;
                    border-color: #cbd5e0;
                    transform: translateY(-1px);
                }

                .paper-info {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .paper-badge {
                    background: #3b82f6;
                    color: white;
                    padding: 0.5rem 1rem;
                    border-radius: 1rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* Summary Cards */
                .summary-grid {
                    margin: 0 auto 1.5rem;
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 1rem;
                }

                .summary-card {
                    background: white;
                    border-radius: 1rem;
                    padding: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s;
                }

                .summary-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
                    border-color: #cbd5e0;
                }

                .summary-icon {
                    width: 3rem;
                    height: 3rem;
                    border-radius: 0.75rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .summary-content {
                    flex: 1;
                }

                .summary-title {
                    font-size: 0.75rem;
                    color: #6b7280;
                    font-weight: 500;
                    margin-bottom: 0.25rem;
                }

                .summary-value {
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                /* Tabs */
                .tabs-container {
                    background: white;
                    border-radius: 1rem;
                    margin: 0 auto 2rem;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }

                .tabs-wrapper {
                    display: flex;
                }

                .tab-button {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1rem;
                    background: transparent;
                    border: none;
                    border-bottom: 3px solid transparent;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .tab-button:hover {
                    background: #f8fafc;
                    color: #3b82f6;
                }

                .tab-button.active {
                    color: #3b82f6;
                    border-bottom-color: #3b82f6;
                    background: #eff6ff;
                }

                .tab-icon {
                    font-size: 1rem;
                }

                /* Section Headers */
                .section-header {
                    margin: 0 auto 1.5rem;
                }

                .section-header h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 0.5rem;
                }

                .section-header p {
                    font-size: 0.875rem;
                    color: #64748b;
                }

                /* Subject Cards Grid */
                .subject-cards-grid {
                    margin: 0 auto 2rem;
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
                    gap: 1rem;
                }

                .subject-card {
                    background: white;
                    border-radius: 1rem;
                    padding: 1.25rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f3f4f6;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .subject-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
                    border-color: #e5e7eb;
                }

                .subject-card-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .subject-card-avatar {
                    width: 2.5rem;
                    height: 2.5rem;
                    background: #e0f2fe;
                    color: #0369a1;
                    border-radius: 0.625rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 1rem;
                    margin-right: 0.75rem;
                }

                .subject-card h4 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0 0 0.25rem 0;
                }

                .subject-card p {
                    font-size: 0.75rem;
                    color: #6b7280;
                    margin: 0;
                }

                .subject-stats-grid {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: #f9fafb;
                    border-radius: 0.5rem;
                    margin-bottom: 1rem;
                }

                .stat-item {
                    text-align: center;
                    flex: 1;
                }

                .stat-value {
                    font-size: 1rem;
                    font-weight: 700;
                    margin-bottom: 0.25rem;
                }

                .stat-label {
                    font-size: 0.6875rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .stat-divider {
                    width: 1px;
                    height: 2rem;
                    background: #e5e7eb;
                }

                .subject-expandable {
                    border-top: 1px solid #f1f5f9;
                    padding-top: 1rem;
                }

                .expand-toggle {
                    width: 100%;
                    padding: 0.75rem;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                    color: #475569;
                    font-size: 0.875rem;
                    font-weight: 500;
                    transition: all 0.2s;
                }

                .expand-toggle:hover {
                    background: #f1f5f9;
                }

                .chapters-list {
                    margin-top: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .chapter-item {
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 0.5rem;
                    border: 1px solid #e2e8f0;
                }

                .chapter-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .chapter-header h5 {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0;
                }

                .chapter-questions {
                    background: #10b98120;
                    color: #10b981;
                    padding: 0.25rem 0.5rem;
                    border-radius: 1rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .topics-toggle {
                    width: 100%;
                    padding: 0.5rem;
                    background: transparent;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.375rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                    color: #64748b;
                    font-size: 0.75rem;
                    transition: all 0.2s;
                }

                .topics-toggle:hover {
                    background: #f1f5f9;
                }

                .topics-list {
                    margin-top: 0.75rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .topic-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.5rem;
                    background: white;
                    border-radius: 0.375rem;
                    border: 1px solid #f1f5f9;
                }

                .topic-name {
                    font-size: 0.75rem;
                    color: #475569;
                }

                .topic-meta {
                    font-size: 0.6875rem;
                    color: #64748b;
                    background: #f1f5f9;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.25rem;
                }

                /* Charts Grid */
                .charts-grid {
                    margin: 0 auto 2rem;
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 1.5rem;
                }

                .chart-card {
                    background: white;
                    border-radius: 1rem;
                    padding: 1.5rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                }

                .card-header {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .card-content {
                    padding: 0.5rem 0;
                }

                /* Filter Section */
                .filter-section {
                    display: flex;
                    gap: 1rem;
                    margin: 0 auto 1.5rem;
                    flex-wrap: wrap;
                }

                .search-container {
                    position: relative;
                    flex: 1;
                    min-width: 300px;
                }

                .search-icon {
                    position: absolute;
                    left: 1rem;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                }

                .search-input {
                    width: 100%;
                    padding: 0.75rem 1rem 0.75rem 3rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 0.75rem;
                    font-size: 0.875rem;
                    color: #1e293b;
                    transition: all 0.2s;
                    background: white;
                }

                .search-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .filter-group {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: white;
                    padding: 0.75rem 1rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 0.75rem;
                    min-width: 200px;
                }

                .filter-icon {
                    color: #64748b;
                }

                .subject-select {
                    border: none;
                    background: transparent;
                    font-size: 0.875rem;
                    color: #1e293b;
                    cursor: pointer;
                    outline: none;
                    width: 100%;
                }

                /* Questions Container */
                .questions-container {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .question-card {
                    background: white;
                    border-radius: 1rem;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    transition: all 0.3s;
                }

                .question-card:hover {
                    border-color: #cbd5e0;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                }

                .question-header {
                    padding: 1.25rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    background: #f8fafc;
                    cursor: pointer;
                }

                .question-main {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    flex: 1;
                }

                .question-number {
                    width: 2.5rem;
                    height: 2.5rem;
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    color: white;
                    border-radius: 0.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.875rem;
                    flex-shrink: 0;
                }

                .question-content {
                    flex: 1;
                }

                .question-text-preview {
                    color: #1e293b;
                    font-size: 0.875rem;
                    line-height: 1.5;
                    margin-bottom: 0.5rem;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .question-meta {
                    display: flex;
                    gap: 1rem;
                    font-size: 0.75rem;
                    color: #64748b;
                }

                .meta-item {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .question-actions {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .question-stats {
                    display: flex;
                    gap: 0.75rem;
                }

                .stat-item {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.375rem 0.75rem;
                    border-radius: 1rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .stat-item.correct {
                    background: #10b98120;
                    color: #10b981;
                }

                .stat-item.wrong {
                    background: #ef444420;
                    color: #ef4444;
                }

                .stat-item.unanswered {
                    background: #64748b20;
                    color: #64748b;
                }

                .expand-icon {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 0.25rem;
                    border-radius: 0.25rem;
                    transition: all 0.2s;
                }

                .expand-icon:hover {
                    background: #f1f5f9;
                    color: #64748b;
                }

                /* Question Details */
                .question-details {
                    padding: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                }

                .detail-section {
                    margin-bottom: 1.5rem;
                }

                .detail-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 1rem;
                }

                .detail-content.full-question {
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 0.5rem;
                    font-size: 0.875rem;
                    line-height: 1.6;
                    color: #1e293b;
                }

                /* Options Grid - 2 Column Layout */
                .options-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1rem;
                }

                @media (max-width: 768px) {
                    .options-grid {
                        grid-template-columns: 1fr;
                    }
                }

                .option-item {
                    padding: 1rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 0.5rem;
                    background: white;
                    transition: all 0.2s;
                }

                .option-item:hover {
                    border-color: #cbd5e1;
                }

                .option-item.correct {
                    border-color: #10b981;
                    background: #10b98108;
                }

                .option-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .option-letter {
                    width: 1.75rem;
                    height: 1.75rem;
                    background: #3b82f6;
                    color: white;
                    border-radius: 0.375rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.75rem;
                }

                .option-item.correct .option-letter {
                    background: #10b981;
                }

                .correct-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    background: #10b981;
                    color: white;
                    padding: 0.25rem 0.5rem;
                    border-radius: 1rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .option-content {
                    font-size: 0.875rem;
                    line-height: 1.5;
                    color: #475569;
                }

                /* Detailed Stats Grid */
                .stats-grid.detailed {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                }

                .stat-card.mini {
                    background: white;
                    border-radius: 0.75rem;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    border: 1px solid #e2e8f0;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .stat-card.mini:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    border-color: #cbd5e0;
                }

                .stat-icon-wrapper {
                    flex-shrink: 0;
                }

                .stat-icon {
                    width: 2.5rem;
                    height: 2.5rem;
                    border-radius: 0.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #3b82f620, #3b82f610);
                    color: #3b82f6;
                }

                .stat-icon.correct {
                    background: linear-gradient(135deg, #10b98120, #10b98110);
                    color: #10b981;
                }

                .stat-icon.wrong {
                    background: linear-gradient(135deg, #ef444420, #ef444410);
                    color: #ef4444;
                }

                .stat-icon.unanswered {
                    background: linear-gradient(135deg, #64748b20, #64748b10);
                    color: #64748b;
                }

                /* Student Modal */
                .modal-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 1rem;
                }

                .student-modal {
                    width: 500px;
                    max-width: 100%;
                    max-height: 80vh;
                    background: white;
                    border-radius: 1rem;
                    overflow: hidden;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.25rem 1.5rem;
                    border-bottom: 1px solid #e2e8f0;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1f2937;
                }

                .modal-header button {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #64748b;
                }

                .modal-tabs {
                    display: flex;
                    gap: 0.5rem;
                    padding: 1rem 1.5rem;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .modal-tab {
                    flex: 1;
                    padding: 0.75rem;
                    border-radius: 0.5rem;
                    border: none;
                    background: #e2e8f0;
                    font-size: 0.875rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .modal-tab.active {
                    background: #3b82f6;
                    color: white;
                }

                .modal-content {
                    max-height: 400px;
                    overflow-y: auto;
                    padding: 1.5rem;
                }

                .students-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .student-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                }

                .student-info {
                    flex: 1;
                }

                .student-info strong {
                    display: block;
                    font-size: 0.875rem;
                    color: #1f2937;
                    margin-bottom: 0.25rem;
                }

                .muted {
                    font-size: 0.75rem;
                    color: #64748b;
                }

                .view-button {
                    padding: 0.5rem 1rem;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 0.5rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .view-button:hover {
                    background: #2563eb;
                }

                /* Empty State */
                .empty-state {
                    text-align: center;
                    padding: 3rem 2rem;
                    color: #94a3b8;
                }

                .empty-icon {
                    font-size: 3rem;
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }

                .empty-state h3 {
                    font-size: 1.25rem;
                    color: #64748b;
                    margin: 0 0 0.5rem;
                }

                .empty-state p {
                    margin: 0;
                    font-size: 0.875rem;
                }

                /* Clickable */
                .clickable {
                    transition: all 0.2s;
                    cursor: pointer;
                }

                .clickable:hover {
                    opacity: 0.9;
                }

                /* Color Utilities */
                .text-blue-600 { color: #3b82f6; }
                .text-green-600 { color: #16a34a; }
                .text-red-600 { color: #dc2626; }
                .text-indigo-600 { color: #4f46e5; }
                .text-orange-600 { color: #ea580c; }
                .text-gray-600 { color: #6b7280; }
                
                .bg-blue-100 { background-color: #dbeafe; color: #1d4ed8; }
                .bg-green-100 { background-color: #dcfce7; color: #16a34a; }
                .bg-red-100 { background-color: #fee2e2; color: #dc2626; }
                .bg-indigo-100 { background-color: #e0e7ff; color: #4f46e5; }
                .bg-orange-100 { background-color: #ffedd5; color: #ea580c; }

                /* Responsive Design */
                @media (max-width: 1024px) {
                    .dashboard-container {
                        padding: 1rem;
                    }

                    .summary-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .subject-cards-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .dashboard-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }

                    .header-controls {
                        width: 100%;
                        justify-content: space-between;
                    }

                    .summary-grid {
                        grid-template-columns: 1fr;
                    }

                    .subject-cards-grid {
                        grid-template-columns: 1fr;
                    }

                    .filter-section {
                        flex-direction: column;
                    }

                    .search-container {
                        min-width: auto;
                    }

                    .stats-grid.detailed {
                        grid-template-columns: 1fr;
                    }

                    .question-header {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .question-actions {
                        width: 100%;
                        justify-content: space-between;
                    }
                }

                @media (max-width: 480px) {
                    .dashboard-container {
                        padding: 0.75rem;
                    }

                    .subject-stats-grid {
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .stat-divider {
                        display: none;
                    }

                    .question-stats {
                        flex-wrap: wrap;
                        gap: 0.5rem;
                    }

                    .stat-item {
                        flex: 1;
                        min-width: 80px;
                        justify-content: center;
                    }

                    .modal-tabs {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default PaperOverview;